<template>
  <div class="task-detail">
    <a-spin :spinning="loading">
      <div class="task-header">
        <div class="task-title-section">
          <h3>{{ displayTitle }}</h3>
          <div class="task-meta">
            <a-tag :color="getTaskStatusColor(String(task.status))" size="large">
              {{ t(`task.${getTaskStatusI18nKey(String(task.status))}`) }}
            </a-tag>
            <a-tag>
              {{ t(`task.${getTaskPurposeI18nKey(String(task.task_purpose))}`) }}
            </a-tag>
            <a-tag v-if="task.round > 1" color="purple">
              {{ t('task.roundBadge', { n: task.round }) }}
            </a-tag>
          </div>
        </div>
      </div>

      <a-divider />

      <a-descriptions :column="2" size="small" bordered>
        <a-descriptions-item :label="t('task.taskId')">
          {{ task.id }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.workItemId')">
          {{ task.work_item_id }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.creator')">
          {{ formatTaskUserLabel(task.creator, task.creator_id) }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.assignee')">
          <span v-if="task.assignee_id || task.assignee">
            {{ formatTaskUserLabel(task.assignee, task.assignee_id) }}
          </span>
          <span v-else>{{ t('task.unassigned') }}</span>
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.round')">
          {{ task.round }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.createTime')">
          {{ formatDateTime(task.created_at) }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.startedAt')" v-if="task.started_at">
          {{ formatDateTime(task.started_at) }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.submittedAt')" v-if="task.submitted_at">
          {{ formatDateTime(task.submitted_at) }}
        </a-descriptions-item>
        <a-descriptions-item :label="t('task.completeTime')" v-if="task.completed_at">
          {{ formatDateTime(task.completed_at) }}
        </a-descriptions-item>
        <a-descriptions-item
          v-if="task.status === TaskStatus.REJECTED_AT_ASSIGNMENT && task.rejection_reason"
          :label="t('task.rejectionReason')"
          :span="2"
        >
          {{ task.rejection_reason }}
        </a-descriptions-item>
      </a-descriptions>

      <div class="workspace-section">
        <a-space>
          <a-button
            v-if="role === 'assignee' && task.status === TaskStatus.PROCESSING"
            type="primary"
            :loading="openingWorkspace"
            @click="handleOpenWorkspace"
          >
            {{ t('task.workOnItem') }}
          </a-button>
          <a-button :loading="openingWorkspace" @click="handleOpenWorkspace">
            {{ t('task.viewWorkItem') }}
          </a-button>
        </a-space>
      </div>

      <div v-if="reviewRecords.length" class="review-section">
        <h4>{{ t('task.reviewRecords') }}</h4>
        <a-timeline>
          <a-timeline-item
            v-for="record in reviewRecords"
            :key="record.id"
            color="orange"
          >
            <div class="timeline-content">
              <div class="timeline-title">
                {{ t('task.roundBadge', { n: record.round }) }}
              </div>
              <div class="timeline-time">{{ formatDateTime(record.created_at) }}</div>
              <div class="timeline-desc">{{ record.comment }}</div>
            </div>
          </a-timeline-item>
        </a-timeline>
      </div>
      <a-empty
        v-else-if="!loading"
        :description="t('task.noReviewRecords')"
        :image="false"
      />
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

import { TaskStatus } from '@/api/common-types'
import {
  getTaskReviewRecords,
  type TaskResponse,
  type TaskReviewRecord,
} from '@/modules/task/api'
import {
  getTaskDisplayTitle,
  getTaskPurposeI18nKey,
  getTaskStatusColor,
  getTaskStatusI18nKey,
  formatTaskUserLabel,
  openTaskWorkspace,
} from '@/modules/task/utils/task-display'

const props = withDefaults(
  defineProps<{
    task: TaskResponse
    /** assignee = 收到的任务；creator = 指派的任务 */
    role?: 'assignee' | 'creator'
  }>(),
  {
    role: 'assignee',
  },
)

const emit = defineEmits<{
  update: []
  close: []
}>()

const router = useRouter()
const { t } = useI18n()

const loading = ref(false)
const openingWorkspace = ref(false)
const reviewRecords = ref<TaskReviewRecord[]>([])

const displayTitle = computed(() =>
  getTaskDisplayTitle(props.task, t('task.taskFallbackTitle', { id: props.task.id })),
)

watch(
  () => props.task.id,
  () => {
    void loadReviewRecords()
  },
  { immediate: true },
)

async function loadReviewRecords() {
  loading.value = true
  try {
    if (props.task.review_records?.length) {
      reviewRecords.value = [...props.task.review_records].sort((a, b) => a.round - b.round)
      return
    }
    const records = await getTaskReviewRecords(props.task.id)
    reviewRecords.value = [...(records ?? [])].sort((a, b) => a.round - b.round)
  } catch (error) {
    console.error('加载打回记录失败:', error)
    reviewRecords.value = []
  } finally {
    loading.value = false
  }
}

async function handleOpenWorkspace() {
  openingWorkspace.value = true
  try {
    await openTaskWorkspace(router, props.task)
    emit('close')
  } catch (error) {
    console.error('打开条目工作页失败:', error)
  } finally {
    openingWorkspace.value = false
  }
}

function formatDateTime(value?: string | null) {
  if (!value) return '—'
  return dayjs(value).format('YYYY-MM-DD HH:mm:ss')
}
</script>

<style lang="less" scoped>
.task-detail {
  padding: 16px 0;
}

.task-header {
  margin-bottom: 16px;

  .task-title-section h3 {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: #262626;
  }

  .task-meta {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
}

.workspace-section {
  margin-top: 20px;
}

.review-section {
  margin-top: 24px;

  h4 {
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 600;
    color: #262626;
    border-bottom: 1px solid #f0f0f0;
    padding-bottom: 8px;
  }
}

.timeline-content {
  padding-left: 8px;

  .timeline-title {
    font-weight: 600;
    color: #262626;
    margin-bottom: 4px;
  }

  .timeline-time {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 4px;
  }

  .timeline-desc {
    font-size: 14px;
    color: #595959;
    white-space: pre-wrap;
  }
}

:deep(.ant-descriptions-item-label) {
  font-weight: 500;
  color: #8c8c8c;
}

:deep(.ant-descriptions-item-content) {
  color: #262626;
}
</style>
